import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configurationService, SupportActionPermissions } from '@/services/configuration-service';
import { ConfigurationModel } from '@/models/configurationModel';

export function useCanAccessConfiguration(companyId: number | undefined) {
  return useQuery({
    queryKey: ['canAccessConfiguration', companyId],
    queryFn: () => configurationService.canAccessConfiguration(companyId!),
    enabled: !!companyId,
  });
}

export function useConfiguration(companyId: number | undefined) {
  return useQuery({
    queryKey: ['configuration', companyId],
    queryFn: () => configurationService.getConfiguration(companyId!),
    enabled: !!companyId,
  });
}

export function useSaveConfiguration(companyId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configuration: ConfigurationModel) =>
      configurationService.saveConfiguration(companyId, configuration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration', companyId] });
    },
  });
}

export function useSupportActions(companyId: number | undefined) {
  return useQuery<SupportActionPermissions>({
    queryKey: ['supportActionPermissions', companyId],
    queryFn: () => configurationService.getSupportActionPermissions(companyId!),
    enabled: !!companyId,
  });
}

export function useLoadAllApplications(companyId: number) {
  return useMutation({
    mutationFn: () => configurationService.loadAllApplications(companyId),
  });
}

export function useUpdateAllApplications(companyId: number) {
  return useMutation({
    mutationFn: () => configurationService.updateAllApplications(companyId),
  });
}

export function useDeleteAllApplications(companyId: number) {
  return useMutation({
    mutationFn: () => configurationService.deleteAllApplications(companyId),
  });
}

export function useInitializeStaticData(companyId: number) {
  return useMutation({
    mutationFn: () => configurationService.initializeStaticData(companyId),
  });
}
